import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { IDropdownConfiguration, IDropdownEntityItem } from '../models';

const StoreBase = stores.base.StoreBase<IConfiguration, IComponentStyles>;
type QueryStore = stores.QueryStore<IConfiguration, IComponentStyles>;

const { isNullOrUndefined } = common.commonUtils;

export class DropdownStore extends StoreBase {
	static componentKey: 'dropdown' = 'dropdown' as const;

	@observable.ref items: IDropdownEntityItem[] = [];
	@observable.ref selectedItem: IDropdownEntityItem | undefined;
	@observable.ref vHiddenMessage = '';

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);

		makeObservable(this);
	}

	@computed
	get appStore(): AppStore {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get queryStore(): QueryStore {
		return this.storeContext.get<QueryStore>('queryStore');
	}

	@computed
	get configuration(): IDropdownConfiguration | undefined {
		if (this.storeContext && this.appStore) {
			return this.appStore.getComponentConfiguration(DropdownStore.componentKey);
		}
	}

	@action
	onChange(item: any) {
		this.appStore.redirect(item.linkedEntity, {
			event: 'filter select',
			value: item.value,
			filterSelected: item.label,
		});
	}

	@action
	onOpen = () => {
		this.vHiddenMessage =
			'You will be redirected to the funds page when choosing an option in the list of suggestions';
	};

	@action
	onClose = () => {
		this.vHiddenMessage = '';
	};

	@computed
	get props(): components.base.Dropdown.models.IDropdownProps {
		const props: components.base.Dropdown.models.IDropdownProps = {
			items: this.items,
			selectedItem: this.selectedItem,
			onChange: this.onChange.bind(this),
			singleItemDisplayOnly: true,
			onOpen: this.onOpen,
			onClose: this.onClose,
		};

		if (this.configuration && this.configuration.label) {
			props.label = this.configuration.label;
		}

		if (this.configuration && this.configuration.placeholder) {
			props.placeholder = this.configuration.placeholder;
		}

		if (this.configuration && !isNullOrUndefined(this.configuration.singleItemDisplayOnly)) {
			props.singleItemDisplayOnly = this.configuration.singleItemDisplayOnly;
		}
		props.hide = this.configuration && this.configuration.hideIfEmpty && this.items.length === 0;
		return props;
	}

	@action
	async initialize(): Promise<void> {
		await this.updateShareClasses();
	}

	@action
	async updateShareClasses() {
		if (this.configuration) {
			const { dataEntitiesId, resultItemLabel, selectedInputKey } = this.configuration;
			const searchResponse = await this.queryStore.entitiesRequest(dataEntitiesId, { term: '' });
			if (searchResponse && searchResponse.total > 0) {
				const clientCode = (this.appStore && this.appStore.getInput(selectedInputKey)) || 'isin';
				const filteredItems = searchResponse.values.map((entity) => {
					const item = {
						value: entity.clientCode,
						label: this.getQueryValue(
							resultItemLabel,
							undefined,
							this.appStore.overrideEntityContext(entity),
						),
						linkedEntity: entity,
					};
					if (entity.clientCode === clientCode) {
						runInAction(() => {
							this.selectedItem = item;
						});
					}
					return item;
				});
				runInAction(() => {
					this.items = filteredItems;
				});
			}
		}
	}
}
